<template>
    <error-boundary
        v-model="appStore.lastKnownError"
        v-bind="errorBoundaryOptions"
        @reload="refresh"
        @error-captured="appStore.registerError"
    >
        <v-app v-bind="vAppOptions">
            <v-layout class="h-100 flex-column">
                <!-- site header -->
                <site-header
                    v-if="!hideHeader"
                    v-bind="siteHeaderOptions"
                    v-model:current-language="appStore.language"
                    :menu-items="appStore.menuItems"
                    :is-logged-in="userStore.isLoggedIn"
                    :profile-info="userStore.profile"
                />

                <!-- sidebar -->
                <slot v-if="appStore.ready" name="sidebar" />

                <!-- site main -->
                <slot v-if="appStore.ready" />

                <!-- site footer -->
                <slot v-if="!hideFooter" name="footer" />
            </v-layout>
            <v-overlay :model-value="!appStore.ready" scrim="white" class="h-100 d-flex align-center justify-center">
                <v-progress-circular data-id="appLoadingProgress" indeterminate color="primary" size="64" />
            </v-overlay>
        </v-app>
        <silent-login v-if="silentLoginEnabled && userLoaded && !userStore.isLoggedIn" />
    </error-boundary>
</template>

<script setup lang="ts">
import { VApp } from 'vuetify/components';
import { ErrorBoundary } from '../ErrorBoundary';
import { SiteHeader } from '../SiteHeader';
import { useUserStore } from '../../features/authentication';
import { useAppStore } from '../../features/common';
import { nextTick, onMounted, provide, ref } from 'vue';
import { AppLayoutProvideKey, UserClaimsKey } from '../../injectionKeys';
import { SilentLogin } from '../../features/authentication';
import { storeToRefs } from 'pinia';

interface Props {
    silentLoginEnabled?: boolean;
    hideHeader?: boolean;
    hideFooter?: boolean;
    errorBoundaryOptions?: NonNullable<InstanceType<typeof ErrorBoundary>['$props']>;
    vAppOptions?: NonNullable<InstanceType<typeof VApp>['$props']>;
    siteHeaderOptions?: NonNullable<InstanceType<typeof SiteHeader>['$props']>;
}

const props = withDefaults(defineProps<Props>(), {
    errorBoundaryOptions: () => ({
        errorsToDisplay: [],
        stopPropagation: true
    })
});

const appStore = useAppStore();
const userStore = useUserStore();
const userLoaded = ref(false);
const { claims } = storeToRefs(userStore);

onMounted(async () => {
    if (props.silentLoginEnabled) {
        try {
            await userStore.loadUserSilent();
            await userStore.loadUser();
            await userStore.loadProfile(appStore.menuItems?.profileLink, appStore.menuItems?.minSideLink);
        } finally {
            userLoaded.value = true;
        }
    }

    nextTick(() => (appStore.ready = true));
});

provide(AppLayoutProvideKey, { registerError: appStore.registerError });
provide(UserClaimsKey, claims);

function refresh() {
    location.reload();
}
</script>
